import type { ProjectListing } from '@/api/v4/projects.api';
import { type ProjectCompaniesSort } from '@/api/v4/projects.api';
import { useListingStages } from '@/features/listing-stage/use-listing-stages.query';
import { modalState } from '@/state/modal.state';
import type { ISort } from '@/ui/table/table.types';

import { getListingStageById } from '../../../listing-stage/helpers/get-listing-stage-by-id';
import { projectIdState } from '../../project.state';
import { projectListingSortState } from '../../project-listing.state';
import { useUpdateProjectListingStage } from '../use-update-project-listing-stage.mutation';

import { useProjectListingsTableConfiguration } from './use-project-listings-table-configuration';
import { InfiniteScrollTable } from '@/ui/table/infinite-table/infinite-scroll-table';
import type { FetchNextPageOptions } from '@tanstack/react-query';
import { StageManagementEmptyState } from '../stage-management-table/stage-management-empty-state';
import { TabTableFilters } from '@/components/tab-table-filters/tab-table-filters';
import { Stack } from '@/ui/line/line';
import type { TabStage } from '../stage-management-table/use-stage-tabs-config';

import { styled } from 'goober';
import { useAtomValue, useSetAtom } from 'jotai';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { useProjectListingsTabParam } from './use-project-listings-tab-param';
import { useSplitListingStageTabDisplay } from './use-split-listing-stage-tab-display';
import { PendingProjectsTable } from '../pending-projects/pending-projects-table';

export const ProjectListingsTable = ({
  hasNextPage,
  isDisabledMode,
  isFetchingNextPage,
  isLoading,
  listingsData,
  onLoadMore,
}: {
  hasNextPage: boolean;
  isDisabledMode?: boolean;
  isFetchingNextPage?: boolean;
  isLoading: boolean;
  listingsData: ProjectListing[];
  onLoadMore: (options?: FetchNextPageOptions) => Promise<unknown>;
}) => {
  const projectId = useAtomValue(projectIdState);
  const setModal = useSetAtom(modalState);
  const { listingStages } = useListingStages();
  const { listingsTabParam, setListingsTabParam } =
    useProjectListingsTabParam();

  const setSort = useSetAtom(projectListingSortState);

  const { editProjectListingStage } = useUpdateProjectListingStage();
  const { mainPanelTabs, secondaryPanelTabs } =
    useSplitListingStageTabDisplay();

  const handleListingStageUpdate = async (
    listingId: string,
    newListingStageId: string | null,
    isDeal?: boolean,
  ) => {
    if (isDeal) {
      setModal({
        state: 'deal',
        listingId: listingId,
      });
    }

    if (!newListingStageId) return;
    const newStage = getListingStageById(newListingStageId, listingStages);

    if (!newStage) return;
    await editProjectListingStage({
      newProjectListingStageId: newListingStageId,
      projectListingId: listingId,
    });
  };

  const handleSort = (update: ISort<ProjectCompaniesSort>) => {
    setSort({ direction: update.direction, sort: update.sort });
  };

  const { headers, rows } = useProjectListingsTableConfiguration({
    data: listingsData,
    onListingStatusChanged: handleListingStageUpdate,
  });

  if (!projectId && !isDisabledMode) {
    return null;
  }

  const tableHeight = listingsData.length > 6 ? '800px' : '400px';
  const isEmptyResults = !listingsData.length && !isLoading;

  if (isDisabledMode) {
    return (
      <StageManagementEmptyState
        isProjectEmpty
        projectId={projectId ?? undefined}
      />
    );
  }

  return (
    <Stack>
      <Flexbox justify="space-between">
        <TableFilters
          filters={mainPanelTabs}
          onFilterClick={filter => setListingsTabParam(filter.id)}
        />

        <TableFilters
          filters={secondaryPanelTabs}
          onFilterClick={filter => setListingsTabParam(filter.id)}
        />
      </Flexbox>
      {listingsTabParam === 'pending-companies' ? (
        <PendingProjectsTable isDisabledMode={isDisabledMode} />
      ) : isEmptyResults ? (
        <StageManagementEmptyState
          isProjectEmpty
          projectId={projectId ?? undefined}
        />
      ) : (
        <InfiniteScrollTable
          data={listingsData}
          dataTestId="project-listing-table"
          hasMore={hasNextPage}
          headers={headers}
          height={tableHeight}
          id="project-listing"
          isFetchingNextPage={isFetchingNextPage}
          isLoading={isLoading}
          onLoadMore={onLoadMore}
          onSort={handleSort}
          rows={rows}
          sortState={projectListingSortState}
          tableBorderRadius="8px"
          tableShadow
        />
      )}
    </Stack>
  );
};

const TableFilters = styled(
  TabTableFilters<TabStage | { id: string; name: string }>,
)`
  padding: 17px 10px;
  display: flex;
  gap: 30px;
`;
